import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Loader2, ArrowRightLeft, CheckCircle2, XCircle, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/Ui";
import SeatMap from "@/components/SeatMap";
import PrintButtons from "@/components/PrintButtons";
import CancelReceiptView from "@/components/CancelReceiptView";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { money, fmtTime, todayStr, statusStyle, METHOD_LABEL } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import CancellationHistory from "@/components/CancellationHistory";

export function useLookup() {
  const [code, setCode] = useState("");
  const [b, setB] = useState(null);
  const find = async (c = code) => {
    try { const { data } = await api.get(`/bookings/${c.trim().split("-")[0]}`); setB(data); } catch (e) { setB(null); toast.error(errMsg(e)); }
  };
  return { code, setCode, b, setB, find };
}

export function LookupBar({ l, testid }) {
  return (
    <form onSubmit={(e) => { e.preventDefault(); l.find(); }} className="flex max-w-lg gap-2">
      <Input value={l.code} onChange={(e) => l.setCode(e.target.value)} placeholder="Booking code or ticket no. e.g. GPAB12CD" className="font-mono uppercase" data-testid={`${testid}-code-input`} />
      <Button type="submit" className="bg-[#0052FF] hover:bg-[#0041CC]" data-testid={`${testid}-search-button`}><Search className="h-4 w-4" /></Button>
    </form>
  );
}

export function BookingCard({ b }) {
  const t = b.trip || {};
  return (
    <div className="card-soft flex flex-wrap items-center gap-4 p-4" data-testid="desk-booking-card">
      <div className="font-mono text-lg font-bold">{b.code}</div>
      <div className="min-w-0 flex-1 text-sm">
        <div className="font-bold">{t.origin_city} → {t.destination_city} · {t.departure?.slice(0, 10)} {fmtTime(t.departure)}</div>
        <div className="text-xs text-slate-500">Bus {t.bus_number} · Seats {b.passengers.map((p) => p.seat).join(", ")} · {b.passengers.map((p) => p.name).join(", ")}</div>
        <div className="text-xs text-slate-500">{b.channel === "online" ? "Booked online" : `Sold by ${b.teller_name}`} · {METHOD_LABEL[b.payment_method] || b.payment_method} · {b.payment_status}</div>
      </div>
      <b>{money(b.total)}</b>
      <StatusBadge status={b.status} cls={statusStyle[b.status]} />
    </div>
  );
}

export function TransferPanel({ b, onDone }) {
  const tripDate = b.trip?.departure?.slice(0, 10);
  const [date, setDate] = useState(tripDate && tripDate >= todayStr() ? tripDate : todayStr());
  const [opts, setOpts] = useState(null);
  const [pick, setPick] = useState(null);
  const [seats, setSeats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rk, setRk] = useState(0);
  useEffect(() => {
    setPick(null); setSeats([]); setOpts(null);
    api.get(`/tickets/${b.code}/transfer-options`, { params: { date } }).then((r) => setOpts(r.data)).catch((e) => { setOpts([]); toast.error(errMsg(e)); });
  }, [b.code, date, rk]);
  const toggle = (s) => setSeats((x) => (x.includes(s) ? x.filter((y) => y !== s) : x.length < b.passengers.length ? [...x, s] : x));
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/tickets/${b.code}/transfer`, { trip_id: pick.id, seats }); toast.success(`Moved to ${fmtTime(data.trip.departure)} · seats ${seats.join(", ")}. New ticket SMS sent.`); setPick(null); setSeats([]); setRk((k) => k + 1); onDone(data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!["confirmed", "reserved"].includes(b.status)) return <p className="card-soft p-4 text-sm text-slate-500" data-testid="transfer-not-allowed">A {b.status} ticket cannot be transferred.</p>;
  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_auto]">
      <div className="space-y-3">
        <div className="flex items-center gap-3"><Label className="text-xs font-semibold text-slate-600">Trips on</Label><Input type="date" min={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="transfer-date-input" /></div>
        {opts === null && <Loader2 className="h-5 w-5 animate-spin" />}
        {opts?.length === 0 && <p className="card-soft p-4 text-sm text-slate-500" data-testid="transfer-no-options">No trip with {b.passengers.length} free seat(s) on this date.</p>}
        {opts?.map((t) => (
          <button key={t.id} type="button" onClick={() => { setPick(t); setSeats([]); }} data-testid={`transfer-option-${t.id}`}
            className={`card-soft btn-press flex w-full items-center gap-4 p-3 text-left ${pick?.id === t.id ? "ring-2 ring-[#0052FF]" : ""}`}>
            <span className="font-display text-xl font-extrabold">{fmtTime(t.departure)}</span>
            <span className="flex-1 text-sm"><b>{t.origin_city} → {t.destination_city}</b>{t.same ? <span className="chip ml-1 bg-sky-50 text-sky-700">Same bus · reseat</span> : null}<div className="text-xs text-slate-500">{t.bus_type} · Bus {t.bus_number}</div></span>
            <span className="text-xs font-semibold text-emerald-700">{t.seats_available} free</span>
          </button>
        ))}
      </div>
      {pick && (
        <div className="space-y-3 text-center" data-testid="transfer-seat-panel">
          <div className="label-xs">Pick {b.passengers.length} seat(s) · {seats.length} chosen</div>
          <SeatMap capacity={pick.capacity} layout={pick.layout} plan={pick.plan} taken={pick.taken_seats} selected={seats} onToggle={toggle} />
          <Button disabled={busy || seats.length !== b.passengers.length} onClick={save} className="w-full rounded-xl bg-[#0052FF] font-bold hover:bg-[#0041CC]" data-testid="transfer-confirm-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ArrowRightLeft className="h-4 w-4" />Transfer passenger</>}
          </Button>
        </div>
      )}
    </div>
  );
}

export function PassengerTransfer() {
  const l = useLookup();
  return (
    <div className="space-y-5" data-testid="passenger-transfer">
      <h2 className="font-display text-2xl font-extrabold tracking-tight">Passenger transfer</h2>
      <p className="text-sm text-slate-500">Move a passenger to an empty seat on another trip on the same journey. The fare already paid stays the same.</p>
      <LookupBar l={l} testid="transfer" />
      {l.b && <BookingCard b={l.b} />}
      {l.b && <TransferPanel b={l.b} onDone={l.setB} />}
    </div>
  );
}

const DESK_REASONS = [["customer_request", "Customer request", "Charge on fare: 10% if 12h+ before, 20% at 6-12h, 50% under 6h. Insurance returned in full."], ["trip_not_performed", "Trip not performed", "0% charge - bus did not run; fare + insurance returned"], ["trip_not_operated", "Trip not operated", "0% charge - online tickets only, full refund"]];

export function CancelForm({ b, reasons, onDone }) {
  const [reason, setReason] = useState(reasons[0][0]);
  const [note, setNote] = useState("");
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setQ(null); setErr("");
    api.get(`/tickets/${b.code}/cancel-quote`, { params: { reason } }).then((r) => setQ(r.data)).catch((e) => setErr(errMsg(e)));
  }, [b.code, reason]);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/tickets/${b.code}/cancel`, { reason, note }); if (data.pending_cancel || data.approval?.status === "pending") { toast.success("Cancellation sent for admin approval"); } else { toast.success(`Ticket cancelled${data.refund_amount ? ` · refund ${money(data.refund_amount)}` : ""}`); } onDone(data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-soft space-y-4 p-4" data-testid="cancel-form">
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {reasons.map(([k, label, hint]) => (
          <button key={k} type="button" onClick={() => setReason(k)} data-testid={`cancel-reason-${k}`}
            className={`btn-press rounded-xl border p-3 text-left ${reason === k ? "border-[#0052FF] bg-blue-50" : "border-slate-200 bg-white"}`}>
            <div className="text-sm font-bold">{label}</div><div className="text-xs text-slate-500">{hint}</div>
          </button>
        ))}
      </div>
      {err && <p className="rounded-xl bg-rose-50 p-3 text-sm font-semibold text-rose-700" data-testid="cancel-quote-error">{err}</p>}
      {q && (
        <div className="grid grid-cols-3 gap-3 rounded-2xl bg-slate-50 p-4 text-sm" data-testid="cancel-quote">
          <div><div className="label-xs">Ticket total</div><b>{money(q.total)}</b></div>
          <div><div className="label-xs">Charge ({q.percent}%)</div><b className="text-rose-600" data-testid="cancel-quote-fee">{money(q.fee)}</b></div>
          <div><div className="label-xs">Refund</div><b className="text-emerald-700" data-testid="cancel-quote-refund">{money(q.refund)}</b></div>
          {q.fare_amount != null && (
            <div className="col-span-3 text-xs text-slate-500" data-testid="cancel-quote-breakdown">
              Fare {money(q.fare_amount)} (charge applies here only){q.insurance_amount ? ` · insurance ${money(q.insurance_amount)} returned in full` : ""}{q.online_fee_amount ? ` · online fee ${money(q.online_fee_amount)} non-refundable` : ""}.
            </div>
          )}
          <div className="col-span-3 text-xs text-slate-500">{!q.paid ? "Not paid - seats are simply released." : q.refund_route === "teller_cash" ? "Cash ticket - the refund shows as minus money in the selling teller's collection." : q.refund_route === "gateway" ? "Online payment - the refund is sent back to the customer through the payment gateway." : "No refund."} {q.hours_left > 0 ? `${q.hours_left}h before departure.` : "Departure time has passed."}</div>
        </div>
      )}
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason / note (required)" data-testid="cancel-note-input" />
      <Button disabled={busy || !q || note.trim().length < 3} onClick={save} className="rounded-xl bg-rose-600 font-bold hover:bg-rose-700" data-testid="cancel-confirm-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cancel ticket"}
      </Button>
    </div>
  );
}

export function CancelReceipt({ booking }) {
  return (
    <div className="card-soft space-y-3 p-4" data-testid="cancel-receipt-block">
      <div className="label-xs">Cancellation receipt — print for the customer if they need one</div>
      <PrintButtons testPrefix="cancel-receipt-print">{(f) => <CancelReceiptView booking={booking} format={f} />}</PrintButtons>
      <CancelReceiptView booking={booking} format="a4" />
    </div>
  );
}

function Approvals({ isAdmin, pendingOnly }) {
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get("/admin/cancellations", { params: pendingOnly ? { approval: "pending" } : {} }).then((r) => setList(r.data)), [pendingOnly]);
  useEffect(() => { load(); }, [load]);
  const decide = async (b, approved) => {
    try { await api.post(`/admin/cancellations/${b.code}/approve`, { approved }); toast.success(approved ? "Approved" : "Rejected"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!list.length) return <p className="card-soft p-6 text-center text-sm text-slate-500" data-testid="approvals-empty">No cancellations awaiting approval.</p>;
  return (
    <div className="card-soft divide-y divide-slate-100" data-testid="cancellations-list">
      <div className="label-xs p-4">Cancellations &amp; approvals</div>
      {list.map((b) => (
        <div key={b.id} className="flex flex-wrap items-center gap-3 p-4 text-sm" data-testid={`cancellation-row-${b.code}`}>
          <span className="font-mono font-bold">{b.code}</span>
          <span className="min-w-0 flex-1"><b>{b.cancel_reason}</b><div className="text-xs text-slate-500">{b.cancelled_by} ({(b.cancelled_by_role || "").replace("_", " ")}) · {new Date(b.cancelled_at).toLocaleString("en-GB")}</div></span>
          <span className="text-xs">Charge {money(b.cancel_fee || 0)} · Refund <b>{money(b.refund_amount || 0)}</b>{b.refund_status === "pending" ? " (gateway pending)" : ""}</span>
          {b.approval?.status === "pending" && (isAdmin
            ? <span className="flex gap-1"><Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => decide(b, true)} data-testid={`approve-${b.code}`}><CheckCircle2 className="h-4 w-4" />Approve</Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => decide(b, false)} data-testid={`reject-${b.code}`}><XCircle className="h-4 w-4" />Reject</Button></span>
            : <span className="chip bg-amber-50 text-amber-700">Awaiting approval</span>)}
          {b.approval?.status && b.approval.status !== "pending" && <span className={`chip ${b.approval.status === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid={`approval-${b.code}`}>{b.approval.status} by {b.approval.by}</span>}
        </div>
      ))}
    </div>
  );
}

function Refunds() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/refunds").then((r) => setList(r.data)); }, []);
  if (!list.length) return null;
  return (
    <div className="card-soft divide-y divide-slate-100" data-testid="refunds-list">
      <div className="label-xs p-4">Online refunds (payment gateway)</div>
      {list.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-3 p-4 text-sm" data-testid={`refund-row-${r.code}`}>
          <span className="font-mono font-bold">{r.code}</span>
          <span className="flex-1 text-xs text-slate-500">{METHOD_LABEL[r.method] || r.method} · {r.phone} · {new Date(r.created_at).toLocaleString("en-GB")} · by {r.requested_by}</span>
          <b>{money(r.amount)}</b>
          <span className="chip bg-amber-50 capitalize text-amber-700">{r.status}{r.live ? "" : " · test mode"}</span>
        </div>
      ))}
    </div>
  );
}

function OnlineSummary() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/cancellations/summary").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  return (
    <div className="grid gap-3 sm:grid-cols-3" data-testid="online-cancel-summary">
      <div className="card-soft p-4"><div className="label-xs">Online charges kept</div><div className="font-display text-2xl font-extrabold" data-testid="online-fees-kept">{money(s.online_fees_kept)}</div></div>
      <div className="card-soft p-4"><div className="label-xs">Online tickets charged</div><div className="font-display text-2xl font-extrabold">{s.online_cancellations_charged}</div></div>
      <div className="card-soft p-4"><div className="label-xs">Online refunds</div><div className="font-display text-2xl font-extrabold">{money(s.online_refunds)}</div></div>
    </div>
  );
}

export function TicketCancellation() {
  const l = useLookup();
  const { user } = useAuth();
  const [key, setKey] = useState(0);
  const reasons = user?.role === "admin"
    ? [["teller_mistake", "Teller mistake (on behalf of teller)", "0% - full refund to the customer"], ...DESK_REASONS]
    : DESK_REASONS;
  return (
    <div className="space-y-5" data-testid="ticket-cancellation">
      <h2 className="font-display text-2xl font-extrabold tracking-tight">Ticket cancellation</h2>
      <Tabs defaultValue="cancel">
        <TabsList className="rounded-xl">
          <TabsTrigger value="cancel" data-testid="admin-cancel-tab">Ticket cancellation</TabsTrigger>
          <TabsTrigger value="approvals" data-testid="admin-cancel-approvals-tab">Pending approvals</TabsTrigger>
          <TabsTrigger value="history" data-testid="admin-cancel-history-tab">History</TabsTrigger>
        </TabsList>
        <TabsContent value="cancel" className="space-y-5">
          <p className="text-sm text-slate-500">No ticket can be cancelled once the bus has been signed out / dispatched, its departure time has passed, or the passenger has boarded. Insurance is always returned in full and the online convenience fee is never refunded - the charge applies to the fare only. Charges kept go to the selling agent's collection, not the trip revenue split.</p>
          {user?.role === "admin" && <OnlineSummary key={`s${key}`} />}
          <LookupBar l={l} testid="cancel" />
          {l.b && <BookingCard b={l.b} />}
          {l.b && ["confirmed", "reserved"].includes(l.b.status) && l.b.payment_method === "cash" && (
            <div className="card-soft flex items-start gap-3 p-4" data-testid="cash-teller-only-notice">
              <Lock className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
              <div className="text-sm text-slate-600">This is a <b>cash-paid</b> ticket. Cash tickets can only be cancelled by a <b>teller at the counter</b> (they hand the cash back). Ask the selling teller to cancel it from the teller app.</div>
            </div>
          )}
          {l.b && ["confirmed", "reserved"].includes(l.b.status) && l.b.payment_method !== "cash" && <CancelForm b={l.b} reasons={reasons} onDone={(d) => { l.setB(d); setKey((k) => k + 1); }} />}
          {l.b?.status === "cancelled" && <CancelReceipt booking={l.b} />}
        </TabsContent>
        <TabsContent value="approvals" className="space-y-5">
          <p className="text-sm text-slate-500">Teller cancellations of type "My mistake" and "Trip not performed" wait here for an admin to approve or reject.</p>
          <Approvals key={`a${key}`} isAdmin={user?.role === "admin"} pendingOnly />
          {user?.role === "admin" && <Refunds key={`r${key}`} />}
        </TabsContent>
        <TabsContent value="history"><CancellationHistory showCancelledBy /></TabsContent>
      </Tabs>
    </div>
  );
}
